import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { loadRazorpay } from "@/lib/razorpay";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function MembershipsPage({ onOpenAuth }) {
    const { user } = useAuth();
    const isAuthed = user && typeof user === "object";
    const [plans, setPlans] = useState([]);
    const [active, setActive] = useState(null);
    const [processing, setProcessing] = useState(null);

    useEffect(() => {
        api.get("/membership-plans").then(({ data }) => setPlans(data));
    }, []);
    useEffect(() => {
        if (!isAuthed) { setActive(null); return; }
        api.get("/me/membership").then(({ data }) => setActive(data?.active ? data.membership : null));
    }, [isAuthed]);

    const subscribe = async (plan) => {
        if (!isAuthed) { onOpenAuth?.(); return; }
        try {
            setProcessing(plan.id);
            const { data } = await api.post("/payments/membership/create-order", { plan_id: plan.id });
            await loadRazorpay();
            const rzp = new window.Razorpay({
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                name: "Aham Yog · Membership",
                description: plan.name,
                order_id: data.razorpay_order_id,
                prefill: { name: data.user?.name, email: data.user?.email },
                theme: { color: "#18addd" },
                handler: async (resp) => {
                    try {
                        await api.post("/payments/membership/verify", {
                            order_id: data.order_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        toast.success(`Welcome to ${plan.name}!`);
                        const { data: refreshed } = await api.get("/me/membership");
                        setActive(refreshed?.active ? refreshed.membership : null);
                    } catch (err) {
                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                    } finally {
                        setProcessing(null);
                    }
                },
                modal: { ondismiss: () => setProcessing(null) },
            });
            rzp.open();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
            setProcessing(null);
        }
    };

    return (
        <div className="min-h-screen bg-paper" data-testid="memberships-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main>
                <section className="pt-24 pb-12 bg-gradient-to-br from-brand-50 to-paper">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-50 text-accent-600 text-xs font-semibold tracking-wide uppercase">
                            <Sparkles className="h-3.5 w-3.5" /> Membership
                        </span>
                        <h1 className="mt-5 font-heading text-4xl sm:text-5xl font-semibold tracking-tight">
                            One subscription, unlimited practice.
                        </h1>
                        <p className="mt-4 text-lg text-ink-700 max-w-2xl mx-auto">
                            Become a member to unlock our members-only courses, members-only events and priority studio bookings.
                        </p>
                    </div>
                </section>

                {active && (
                    <section className="py-6">
                        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="rounded-3xl bg-brand-50 border border-brand-200 p-5 flex flex-wrap items-center gap-3 justify-between" data-testid="active-membership-banner">
                                <div className="flex items-center gap-3">
                                    <ShieldCheck className="h-6 w-6 text-brand-700" />
                                    <div>
                                        <div className="font-semibold text-brand-700">{active.plan_name} · Active</div>
                                        <div className="text-xs text-ink-700">Renews / expires on {String(active.expires_at).slice(0, 10)}</div>
                                    </div>
                                </div>
                                <Link to="/account" className="text-sm font-semibold text-brand-700 hover:text-brand-800">
                                    Manage in account →
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                <section className="py-12 md:py-16">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        {plans.length === 0 ? (
                            <p className="text-center text-ink-700 py-10">No active membership plans right now — check back soon.</p>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {plans.map((p, i) => (
                                    <article key={p.id} className="rounded-3xl bg-white border border-gray-100 p-7 shadow-soft hover:shadow-brand transition" data-testid={`plan-card-${p.id}`}>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Sparkles className="h-4 w-4 text-accent-500" />
                                            <span className="text-xs uppercase tracking-wider text-ink-400">{p.duration_days} days</span>
                                        </div>
                                        <h3 className="font-heading text-2xl font-semibold">{p.name}</h3>
                                        {p.description && <p className="mt-2 text-sm text-ink-700">{p.description}</p>}
                                        <div className="mt-5 font-heading text-4xl font-semibold text-ink-900">
                                            ₹{Number(p.price).toLocaleString("en-IN")}
                                        </div>
                                        <div className="text-xs text-ink-700">one-time · valid for {p.duration_days} days</div>
                                        {p.benefits?.length > 0 && (
                                            <ul className="mt-5 space-y-2.5">
                                                {p.benefits.map((b, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-ink-900">
                                                        <span className="mt-0.5 h-5 w-5 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                                                            <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                                                        </span>
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <Button
                                            disabled={processing === p.id || (active && active.plan_id === p.id)}
                                            onClick={() => subscribe(p)}
                                            className="mt-7 w-full rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta py-5"
                                            data-testid={`subscribe-btn-${p.id}`}
                                        >
                                            <CreditCard className="h-4 w-4 mr-2" />
                                            {processing === p.id ? "Processing…" : (active && active.plan_id === p.id) ? "Currently active" : isAuthed ? "Subscribe with Razorpay" : "Sign in to subscribe"}
                                        </Button>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
